/**
 * ============================================================================
 * ETHEREAL OFFERING - Main Application Entry Point
 * ============================================================================
 *
 * This is the root file that bootstraps the entire React application.
 * It sets up:
 *   1. React 18 with createRoot API
 *   2. Redux store for global state management
 *   3. Wagmi + RainbowKit for Web3 wallet connections
 *   4. React Router for client-side navigation
 *   5. Global visual effects (sparkles, cursor trails, mushrooms)
 *   6. Toast notification system
 *
 * Architecture Overview:
 * ----------------------
 * - Provider Hierarchy: WagmiConfig > RainbowKitProvider > Redux Provider > ToastContainer > App
 * - Lazy Loading: All page components are code-split for better initial load performance
 * - Global Effects: Visual effects are rendered once at the app level, not per-page
 *
 * @author Ethereal Offering Team
 * @version 1.0.0
 */

import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { configureStore } from "@reduxjs/toolkit";
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { WagmiConfig, createConfig, configureChains } from 'wagmi';
import { mainnet, sepolia } from 'wagmi/chains';
import { publicProvider } from 'wagmi/providers/public';
import { RainbowKitProvider, getDefaultWallets } from '@rainbow-me/rainbowkit';
import '@rainbow-me/rainbowkit/styles.css';
import 'animate.css';
import './index.css';

// Core Layout Components
import Navigation from './components/Navigation';
import DynamicPagePadding from './components/DynamicPagePadding';

// Global Visual Effects - These render on every page
import Sparkles from './components/effects/Sparkles';
import CursorTrail from './components/effects/CursorTrail';
import AnimatedMushroomCursor from './components/effects/AnimatedMushroomCursor';
import { SporeContainer } from './components/effects/SporeEffect';
import BottomMushroomLayer from './components/BottomMushroomLayer';
import BrowserDetectionBanner from './components/BrowserDetectionBanner';
import DisclaimerBadge from './components/DisclaimerBadge';
import { ToastContainer } from './components/Toast';

// Redux Slices - Each manages a specific domain of application state
import wallet from "./store/walletSlice";       // Wallet connection state
import ritual from "./store/ritualSlice";       // Ceremonial/ritual tracking
import provider from "./store/reducers/provider"; // Web3 provider state
import tokens from "./store/reducers/tokens";     // Token contract instances
import amm from "./store/reducers/amm";           // AMM/swap contract state
import crowdsale from "./store/reducers/crowdsale"; // Crowdsale contract state

/**
 * Lazy-loaded Page Components
 * ---------------------------
 * Using React.lazy() for code splitting - each page is loaded only when navigated to.
 * This significantly reduces the initial bundle size and improves Time To Interactive (TTI).
 */
const LandingPage = lazy(() => import('./components/LandingPage'));
const DailyShrooms = lazy(() => import('./components/pages/DailyShrooms'));
const CrossBreeding = lazy(() => import('./components/pages/CrossBreeding'));
const Governance = lazy(() => import('./components/pages/Governance'));
const DAO = lazy(() => import('./components/pages/DAO'));
const BreedingLab = lazy(() => import('./components/pages/BreedingLab'));
const AltarBurn = lazy(() => import('./components/pages/AltarBurn'));
const Donate = lazy(() => import('./components/pages/Donate'));
const Swap = lazy(() => import('./components/pages/Swap'));
const Contact = lazy(() => import('./components/pages/Contact'));
const AdminPanel = lazy(() => import('./components/pages/AdminPanel'));
const NotFound404 = lazy(() => import('./components/pages/NotFound404'));

/**
 * Custom Hardhat Local Chain Configuration
 * -----------------------------------------
 * Defines the local development blockchain for testing.
 * Chain ID 31337 is the default for Hardhat Network.
 * This allows the app to connect to a local Hardhat node during development.
 */
const hardhatLocal = {
  id: 31337,
  name: 'Hardhat Local',
  network: 'hardhat',
  nativeCurrency: {
    decimals: 18,
    name: 'Ether',
    symbol: 'ETH',
  },
  rpcUrls: {
    default: { http: ['http://127.0.0.1:8545'] },
    public: { http: ['http://127.0.0.1:8545'] },
  },
  testnet: true,
};

/**
 * Redux Store Configuration
 * -------------------------
 * Central state management for the application.
 *
 * Slices:
 * - wallet: Tracks wallet connection status and address
 * - ritual: Manages ceremonial/ritual state for sacred offerings
 * - provider: Stores the ethers.js provider instance
 * - tokens: Holds token contract instances (EOT, ETHO, PSD)
 * - amm: AMM/TokenSwap contract for liquidity pools
 * - crowdsale: Crowdsale contract for token sales
 *
 * Note: We disable serializableCheck for Web3 objects (providers, contracts)
 * since they contain non-serializable data like BigInt and functions.
 */
export const store = configureStore({
  reducer: {
    wallet,
    ritual,
    provider,
    tokens,
    amm,
    crowdsale
  },

  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Web3 objects contain non-serializable data - ignore them
        ignoredActions: ['provider/setProvider', 'tokens/setContracts', 'amm/setContract', 'crowdsale/setCrowdsaleContract'],
        ignoredActionPaths: ['payload.connection', 'payload.contract', 'payload.0', 'payload.1'],
        ignoredPaths: ['provider.connection', 'tokens.contracts', 'amm.contract', 'crowdsale.contract'],
      },
    }),
});

/**
 * Wagmi Chain Configuration
 * -------------------------
 * Configures which blockchain networks the app can connect to.
 * Order matters: hardhatLocal is first for development priority.
 * publicProvider() uses public RPC endpoints (no API key needed).
 */
const { chains, publicClient, webSocketPublicClient } = configureChains(
  [hardhatLocal, sepolia, mainnet],
  [publicProvider()]
);

/**
 * RainbowKit Wallet Configuration
 * --------------------------------
 * Sets up the wallet connection modal with popular wallets.
 * projectId should be obtained from WalletConnect Cloud for production.
 */
const { connectors } = getDefaultWallets({
  appName: 'Ethereal Offering',
  projectId: 'YOUR_PROJECT_ID', // TODO: Get from WalletConnect Cloud for production
  chains
});

/**
 * Wagmi Client Configuration
 * --------------------------
 * Creates the main Wagmi config that powers wallet connections.
 * autoConnect: true - Automatically reconnects to previously connected wallet
 */
const wagmiConfig = createConfig({
  autoConnect: true,
  connectors,
  publicClient,
  webSocketPublicClient,
});

/**
 * Main App Component
 * ------------------
 * The root component that sets up routing and global UI elements.
 *
 * Structure:
 * 1. BrowserDetectionBanner - Shows warnings for unsupported browsers
 * 2. Global Effects - Visual effects that appear on all pages
 * 3. Navigation - The main navbar
 * 4. Routes - Page components wrapped in Suspense for lazy loading
 *
 * Note: basename uses PUBLIC_URL for GitHub Pages deployment compatibility
 */
function App() {
  return (
    <Router basename={process.env.PUBLIC_URL}>
      {/* Browser/Mobile Detection - Shows banner if Web3 not supported */}
      <BrowserDetectionBanner />

      {/* Global Visual Effects Layer
          These components render once and persist across all pages:
          - DynamicPagePadding: Adjusts content padding based on navbar height
          - Sparkles: Floating particle effects
          - CursorTrail: Glowing trail following mouse cursor
          - AnimatedMushroomCursor: Custom mushroom cursor icon
          - SporeContainer: Floating spore particles
          - BottomMushroomLayer: Decorative mushrooms at page bottom */}
      <DynamicPagePadding />
      <Sparkles />
      <CursorTrail />
      <AnimatedMushroomCursor />
      <SporeContainer />
      <BottomMushroomLayer />

      {/* Main Navigation Bar */}
      <Navigation />

      {/* Development Disclaimer Badge - Dismissible notice about testing phase */}
      <DisclaimerBadge />

      {/* Route Definitions with Lazy Loading
          Suspense shows a loading indicator while page chunks are fetched.
          Each page is a separate bundle loaded on-demand. */}
      <Suspense fallback={<div style={{ padding: 24, textAlign: 'center', color: '#a78bfa' }}>🍄 Loading…</div>}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/daily-shrooms" element={<DailyShrooms />} />
          <Route path="/cross-breeding" element={<CrossBreeding />} />
          <Route path="/governance" element={<Governance />} />
          <Route path="/dao" element={<DAO />} />
          <Route path="/breeding-lab" element={<BreedingLab />} />
          <Route path="/altar" element={<AltarBurn />} />
          <Route path="/donate" element={<Donate />} />
          <Route path="/swap" element={<Swap />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="*" element={<NotFound404 />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

/**
 * Application Mount Point
 * -----------------------
 * Creates the React root and renders the app with all providers.
 *
 * Provider Hierarchy (outermost to innermost):
 * 1. React.StrictMode - Development checks for deprecated patterns
 * 2. WagmiConfig - Web3 connection state
 * 3. RainbowKitProvider - Wallet connection UI
 * 4. Redux Provider - Global application state
 * 5. ToastContainer - Toast notification context
 * 6. App - The actual application
 *
 * This hierarchy ensures that:
 * - Web3 state is available to all components
 * - Redux state is available to all components
 * - Toast notifications can be triggered from anywhere
 */
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <WagmiConfig config={wagmiConfig}>
      <RainbowKitProvider chains={chains}>
        <Provider store={store}>
          <ToastContainer>
            <App />
          </ToastContainer>
        </Provider>
      </RainbowKitProvider>
    </WagmiConfig>
  </React.StrictMode>
);
